import DOMPurify from 'dompurify';

//
// Turn the HTML body Outlook hands us into HTML the task description editor can hold.
//
// Office returns a complete HTML document (doctype, <head> with Word/Outlook <style> blocks,
// conditional comments, VML) and every element typically carries a long inline style= and class=.
// That presentation markup, not the images, is what makes a mail body huge. The API stores the
// description as StringLength(8000) and RichTextEditorMCE compares the *HTML* string length
// against maxLength, so the import has to be reduced to fit with room left over to type.
//
// Best effort: keep the formatting tags, throw away everything that only carries presentation.
// Layout wrappers (div, table, span, font) are unwrapped rather than deleted, so their text
// survives even though the tag does not.
//

//Formatting we keep. Anything else is unwrapped (its text is kept) or dropped.
const allowedTags = [
  'p',
  'br',
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'strike',
  'del',
  'ins',
  'sub',
  'sup',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'pre',
  'code',
  'hr',
  'a',
];

//Only href survives: no style, class, width, bgcolor, align, id or data-* attributes.
const allowedAttributes = ['href'];

//Budget for the imported body. The API caps the description at 8000 characters, so importing the
//full 8000 would leave the user unable to type a single character: RichTextEditorMCE refuses any
//change once the HTML exceeds maxLength. Keep roughly a quarter of the field free.
export const mailBodyHtmlMaxLength = 6000;

//Take the contents of <body> and drop <head>, <style>, <script> and conditional comments.
//Falls back to the input when no <body> is present (Office sometimes returns a fragment).
const getBodyInnerHtml = (html: string): string => {
  const withoutComments = html.replace(/<!--[\s\S]*?-->/g, '');
  const bodyMatch = withoutComments.match(/<body\b[^>]*>([\s\S]*?)<\/body>/i);
  const content = bodyMatch ? bodyMatch[1] : withoutComments;

  return content.replace(/<(style|script)\b[^>]*>[\s\S]*?<\/\1>/gi, '');
};

//Unwrapping a div or a table row would run its text into the next block, so turn those boundaries
//into a line break first. Table cells only get a space: a break per cell shreds the layout.
//Only tags that get unwrapped belong here - replacing the closing tag of a tag we keep
//(h1-h6, p, blockquote, li) would leave it open and swallow the rest of the mail.
const keepBlockBoundaries = (html: string): string => {
  return html
    .replace(/<\/(div|tr|table)>/gi, '<br>')
    .replace(/<\/(td|th)>/gi, ' ')
    .replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>');
};

//Empty paragraphs and leading/trailing breaks are what is left of the stripped layout.
const tidy = (html: string): string => {
  return html
    .replace(/<p>(\s|&nbsp;)*<\/p>/gi, '')
    .replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>')
    .replace(/(\s|&nbsp;|<br\s*\/?>)+$/gi, '')
    .replace(/^(\s|&nbsp;|<br\s*\/?>)+/gi, '')
    .trim();
};

const clean = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: allowedAttributes,
    //unwrap tags that are not allowed but keep the text inside them
    KEEP_CONTENT: true,
  });
};

//Convert an Outlook mail body to sanitized HTML for the task description.
//Images are dropped: inline mail images are cid: references that resolve only inside Outlook.
export const mailBodyToDescriptionHtml = (html: string | undefined): string => {
  if (!html) {
    return '';
  }

  const result = tidy(clean(keepBlockBoundaries(getBodyInnerHtml(html))));
  if (result.length <= mailBodyHtmlMaxLength) {
    return result;
  }

  //Cutting HTML can land inside a tag, so sanitize once more to close and drop what broke.
  //That repair appends the closing tags the cut left open and so grows the string again - give
  //back the overflow and retry until the repaired result really fits.
  let cut = mailBodyHtmlMaxLength;
  while (cut > 0) {
    const candidate = tidy(clean(result.substring(0, cut)));
    if (candidate.length <= mailBodyHtmlMaxLength) {
      return candidate;
    }

    cut -= candidate.length - mailBodyHtmlMaxLength + 16;
  }

  return '';
};
